import i18next from 'i18next'
import {
  Change,
  FullMeetingFragment,
  GovernmentJobTitle,
  MeetingStepViewId,
  MeetingType,
  ObjectivePaymentStatus,
  ObjectiveStatus,
  OneOffPaymentAndObjectivePaymentsWithObjectivesAndTargetBonusAndContractChangesAndCurrentContractQuery,
  TargetCorrectionPaymentAllowedYearsDto,
  TargetCorrectionPaymentDto,
  CurrencyConversionDto,
  Currency,
} from '../../../../../types'
import { retrieveContractStartAndEndDateFromContract } from '../../../../profile/contract/utils/contractUtil'
import { filterObjectivesByTypeStatusAndRefYear, filterObjectivesByTypeStatusAndRefYears } from '../../../okr/util'
import { calculateNewTargetsData } from '../../shared/util'
import { validatePastTargetsData } from './pastTargetsUtil'
import { FlexibleBonusOverride } from '../../shared/targetBonusCalculation-util'
import { existingWrongTCPaymentsForLowEmployee, round2digits } from './paymentsUtil'
import { isGovernmentJobTitleValid } from '../../../contract/helpers'
import { retrieveCurrencyConversionRate } from '../../../../../shared/currencyConversionUtil'

export const canConfirmMeeting = (
  currentYearFlexibleBonusOverride: FlexibleBonusOverride | undefined,
  pastYearFlexibleBonusOverride: FlexibleBonusOverride | undefined,
  flexibleBonusUnknownCurrency: Currency | undefined,
  currentYearFlexibleBonusUnknownCurrency: Currency | undefined,
  meeting: FullMeetingFragment,
  data: OneOffPaymentAndObjectivePaymentsWithObjectivesAndTargetBonusAndContractChangesAndCurrentContractQuery,
  allStepsCompleted: boolean,
  isOneOffPaymentNotAllowed: boolean,
  targetCorrectionPaymentAllowedYears: TargetCorrectionPaymentAllowedYearsDto,
  activeTargetCorrectionPayment: TargetCorrectionPaymentDto | null | undefined,
  existingDevObjectives: boolean,
  hasGovernmentJobTitleReferenceLevel: boolean,
  currencyConversionData: CurrencyConversionDto[],
  skillStatusBlocksMeeting: boolean,
  invalidScoreGrid: boolean,
): {
  cannotConfirm: boolean
  tooltipText: string
} => {
  const isAppraisalUpdate: boolean = meeting.type === MeetingType.AppraisalUpdate
  const { pastTargetsReferenceYear, newTargetsReferenceYear } = meeting.targetsCalculationHelper
  const {
    objectivesInMeeting,
    targetBonusOfYear,
    salaryOfYear,
    contractChangesByEmployee,
    oneOffPaymentByMeetingId,
    currentContract,
    objectivePayment,
    branchOfficeByEmployee,
    isBonusValidationEnabled,
    governmentJobTitlesByEmployeeId,
    contractsInYear,
    newTargetsTargetBonusOfYear,
    newTargetsSalaryOfYear,
    newTargetsContractsInYear,
  } = data

  const paymentDateValid: boolean = isPaymentDateValid(meeting)
  const paymentDateExists: boolean = meeting.objectivePayment ? meeting.objectivePayment.paymentDate : true

  const isOneOffPaymentDateValid = oneOffPaymentByMeetingId ? oneOffPaymentByMeetingId.paymentDateValid : true
  const existingOneOffPaymentToBeRemoved: boolean = isOneOffPaymentNotAllowed && !!oneOffPaymentByMeetingId

  const existingWrongTCPayments = existingWrongTCPaymentsForLowEmployee(
    meeting.appraisalSeason,
    targetCorrectionPaymentAllowedYears,
    activeTargetCorrectionPayment,
  )
  const atLeastOneReferenceYearAvailable =
    targetCorrectionPaymentAllowedYears.currentYearAllowed || targetCorrectionPaymentAllowedYears.previousYearAllowed

  const existingWrongTCPaymentsTooltip = activeTargetCorrectionPayment
    ? atLeastOneReferenceYearAvailable
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeChanged', {
          year: activeTargetCorrectionPayment.referenceYear,
        })
      : i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeRemoved', {
          year: activeTargetCorrectionPayment.referenceYear,
        })
    : ''

  const isTcpPaymentDateValid = !!activeTargetCorrectionPayment ? activeTargetCorrectionPayment.paymentDateValid : true

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)

  // validate PastTargets: check if all objectives received an achieved amount, check if distributed targetBonus equals targetBonusOfYear
  const {
    isMonetaryTargetMissing: tooLessPastTargetBonusProvided,
    tooMuchTargetBonusProvided: tooMuchPastTargetBonusProvided,
    allPastMonetaryObjectivesChecked,
    existingDevObjectivesToBeClosed,
    atLeastOnePastMonetaryTargetWithWrongCurrency,
    existingOutdatedTargets,
  } = validatePastTargetsData(
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    objectivesInMeeting,
    existingDevObjectives,
    targetBonusOfYear,
    salaryOfYear,
    currentContract && currentContract.annualSalary ? currentContract.annualSalary.amount : 0,
    currentContract && currentContract.targetBonus ? currentContract.targetBonus.amount : 0,
    pastTargetsReferenceYear,
    contractChangesByEmployee,
    currencyConversionData,
    currentContract!.currencyForPayments,
    contractStartDate,
    contractEndDate,
    contractsInYear,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
  )

  // check if targetBonusToBePaid is made negative
  const objectivePaymentValue = objectivePayment || {
    id: null,
    meetingId: meeting.id,
    status: ObjectivePaymentStatus.Draft,
    deduction: null,
  }
  const deduction = objectivePaymentValue.deduction ? objectivePaymentValue.deduction : 0

  const toBePaidObjectives = filterObjectivesByTypeStatusAndRefYears(
    objectivesInMeeting,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved],
    [meeting.targetsCalculationHelper.pastTargetsReferenceYear, meeting.targetsCalculationHelper.newTargetsReferenceYear],
  )

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(
    objectivesInMeeting,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.ProposalFromManager],
    newTargetsReferenceYear,
  )

  const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed
  const {
    isMonetaryTargetMissing: tooLessNewTargetBonusProvided,
    tooMuchTargetBonusProvided: tooMuchNewTargetBonusProvided,
    atLeastOneMonetaryTargetWithWrongCurrency,
    unknownCurrenciesForConversion: newTargetsUnknownCurrenciesForConversion,
  } = calculateNewTargetsData(
    allowedNewTargets,
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    currentYearFlexibleBonusOverride,
    objectivesInMeeting,
    newTargetsTargetBonusOfYear,
    newTargetsSalaryOfYear,
    currentContract!.currencyForPayments,
    currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0,
    currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0,
    contractChangesByEmployee,
    isAppraisalUpdate,
    newTargetsReferenceYear,
    contractStartDate,
    currencyConversionData,
    contractEndDate,
    newTargetsContractsInYear,
    currentYearFlexibleBonusUnknownCurrency,
  )

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      [
        ...toBePaidObjectives
          .filter((obj): boolean => {
            if (obj.currency! !== meeting.employee.currentContract!.currencyForPayments) {
              const conversionRate: number | undefined = retrieveCurrencyConversionRate(
                obj.currency!,
                meeting.employee.currentContract!.currencyForPayments as Currency,
                currencyConversionData,
              )
              if (!conversionRate) {
                return true
              }
            }
            return false
          })
          .map((obj) => obj.currency!),
        ...(flexibleBonusUnknownCurrency ? [flexibleBonusUnknownCurrency] : []),
        ...newTargetsUnknownCurrenciesForConversion,
      ].filter((currency): currency is Currency => !!currency),
    ),
  ]

  const targetBonusToBePaid: number = !!unknownCurrenciesForConversion.length
    ? 0
    : toBePaidObjectives.reduce(
        (sum, obj) =>
          obj.achievedAmount
            ? sum +
              Number(
                (
                  obj.achievedAmount *
                  retrieveCurrencyConversionRate(
                    obj.currency!,
                    meeting.employee.currentContract!.currencyForPayments as Currency,
                    currencyConversionData,
                  )!
                ).toFixed(2),
              )
            : sum,
        0,
      )
  const targetBonusToBePaidNegative = !!unknownCurrenciesForConversion.length ? false : round2digits(targetBonusToBePaid - deduction) < 0

  const existingContractChangesForLowEmployee = meeting.requiresSpecialHandlingOfLowTag && contractChangesByEmployee.length > 0
  const existingWrongNewTargets = !allowedNewTargets && monetaryObjectives.length > 0

  const contractChangesWithGovernmentJobTitle: Pick<Change, 'id' | 'status' | 'changes' | 'validFrom' | 'updatedDate'>[] =
    contractChangesByEmployee.length > 0 && hasGovernmentJobTitleReferenceLevel
      ? !governmentJobTitlesByEmployeeId || !governmentJobTitlesByEmployeeId.length
        ? []
        : contractChangesByEmployee.filter((change) => !!JSON.parse(change.changes).governmentJobTitle)
      : []

  const existingChangesWithUnknownGovernmentJobTitles = !contractChangesWithGovernmentJobTitle.length
    ? false
    : contractChangesWithGovernmentJobTitle.some(
        (change) =>
          !isGovernmentJobTitleValid(
            data.governmentJobTitlesByEmployeeId as GovernmentJobTitle[],
            JSON.parse(change.changes).governmentJobTitle,
          ),
      )

  const appraisalConfirmRules =
    tooLessNewTargetBonusProvided ||
    tooMuchNewTargetBonusProvided ||
    tooMuchPastTargetBonusProvided ||
    tooLessPastTargetBonusProvided ||
    !allPastMonetaryObjectivesChecked ||
    !allStepsCompleted ||
    !paymentDateValid ||
    !isOneOffPaymentDateValid ||
    existingDevObjectivesToBeClosed ||
    existingContractChangesForLowEmployee ||
    existingWrongNewTargets ||
    existingOneOffPaymentToBeRemoved ||
    existingOutdatedTargets ||
    invalidScoreGrid ||
    existingChangesWithUnknownGovernmentJobTitles ||
    atLeastOnePastMonetaryTargetWithWrongCurrency ||
    atLeastOneMonetaryTargetWithWrongCurrency ||
    unknownCurrenciesForConversion.length > 0 ||
    skillStatusBlocksMeeting

  const hasNewTargetsDistributionStep = meeting.meetingSteps?.some((s) =>
    [MeetingStepViewId.NewTargets, MeetingStepViewId.DevelopmentAndTargets].includes(s.viewId),
  )

  // an appraisal update meeting does not force the manager to close all active targets, there are no "past targets" either
  const appraisalUpdateConfirmRules =
    (hasNewTargetsDistributionStep && tooLessNewTargetBonusProvided) ||
    (hasNewTargetsDistributionStep && tooMuchNewTargetBonusProvided) ||
    !allStepsCompleted ||
    !paymentDateValid ||
    !isOneOffPaymentDateValid ||
    !isTcpPaymentDateValid ||
    existingContractChangesForLowEmployee ||
    existingWrongNewTargets ||
    existingOneOffPaymentToBeRemoved ||
    existingWrongTCPayments ||
    atLeastOneMonetaryTargetWithWrongCurrency ||
    existingOutdatedTargets ||
    invalidScoreGrid ||
    skillStatusBlocksMeeting ||
    existingChangesWithUnknownGovernmentJobTitles ||
    unknownCurrenciesForConversion.length > 0

  const cannotConfirm = isAppraisalUpdate ? appraisalUpdateConfirmRules : appraisalConfirmRules

  let tooltipText = ''
  if (isAppraisalUpdate) {
    tooltipText = !!unknownCurrenciesForConversion.length
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.unknownConversionRate', {
          currentContractCurrency: meeting.employee.currentContract!.currencyForPayments,
          unknownCurrencies: unknownCurrenciesForConversion.join(', '),
        })
      : existingWrongNewTargets
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongNewTargets')
      : tooMuchNewTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooMuchNewTargetBonusProvidedInAppraisalUpdate')
      : tooLessNewTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooLessNewTargetBonusProvidedInAppraisalUpdate')
      : targetBonusToBePaidNegative
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.targetBonusToBePaidNegative')
      : existingOneOffPaymentToBeRemoved
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOneOffPaymentsForLowEmployee')
      : !isOneOffPaymentDateValid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidOneOffPaymentDate')
      : existingWrongTCPayments
      ? existingWrongTCPaymentsTooltip
      : !isTcpPaymentDateValid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidTargetCorrectionPaymentDate')
      : existingContractChangesForLowEmployee
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingContractChangesForLowEmployee')
      : !paymentDateExists && targetBonusToBePaid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.missingObjectivePaymentDate')
      : !paymentDateValid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidObjectivePaymentDate')
      : existingChangesWithUnknownGovernmentJobTitles
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidGovernmentJobTitle')
      : atLeastOneMonetaryTargetWithWrongCurrency
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.atLeastOneMonetaryTargetWithWrongCurrency')
      : existingOutdatedTargets
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOutdatedTargets')
      : invalidScoreGrid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidScoreGrid')
      : !allStepsCompleted
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.notAllMeetingsStepsCompleted')
      : skillStatusBlocksMeeting
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidEmployeeSkillStatus', {
          employeeName: meeting.employee.legalFirstName,
        })
      : ''
  } else {
    tooltipText = !!unknownCurrenciesForConversion.length
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.unknownConversionRate', {
          currentContractCurrency: meeting.employee.currentContract!.currencyForPayments,
          unknownCurrencies: unknownCurrenciesForConversion.join(', '),
        })
      : tooMuchPastTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooMuchPastTargetBonusProvided')
      : tooLessPastTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooLessPastTargetBonusProvided')
      : !allPastMonetaryObjectivesChecked
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.allPastObjectivesNeedToBeChecked')
      : existingWrongNewTargets
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongNewTargets')
      : tooMuchNewTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooMuchNewTargetBonusProvided')
      : tooLessNewTargetBonusProvided
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.tooLessNewTargetBonusProvided')
      : targetBonusToBePaidNegative
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.targetBonusToBePaidNegative')
      : existingDevObjectivesToBeClosed
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingDevObjectivesToBeClosed')
      : existingOneOffPaymentToBeRemoved
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOneOffPaymentsForLowEmployee')
      : existingContractChangesForLowEmployee
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingContractChangesForLowEmployee')
      : !paymentDateExists && targetBonusToBePaid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.missingObjectivePaymentDate')
      : !paymentDateValid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidObjectivePaymentDate')
      : !isOneOffPaymentDateValid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidOneOffPaymentDate')
      : existingChangesWithUnknownGovernmentJobTitles
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidGovernmentJobTitle')
      : atLeastOnePastMonetaryTargetWithWrongCurrency
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.atLeastOnePastMonetaryTargetWithWrongCurrency')
      : atLeastOneMonetaryTargetWithWrongCurrency
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.atLeastOneMonetaryTargetWithWrongCurrency')
      : existingOutdatedTargets
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOutdatedTargets')
      : invalidScoreGrid
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidScoreGrid')
      : !allStepsCompleted
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.notAllMeetingsStepsCompleted')
      : skillStatusBlocksMeeting
      ? i18next.t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidEmployeeSkillStatus', {
          employeeName: meeting.employee.legalFirstName,
        })
      : ''
  }

  return {
    cannotConfirm,
    tooltipText,
  }
}

export const isPaymentDateValid = (meeting: FullMeetingFragment): boolean => {
  if (meeting.objectivePayment) {
    return meeting.objectivePayment.paymentDateValid
  }
  return true
}
